import DataTable from 'components/ui/DataTable/data-table';
import PrimaryButton from 'components/ui/PrimaryButton';
import React, { FC, useState } from 'react';
import ViewIcon from '../../../assets/images/icons/view.png';
import { Cell } from 'react-table';
import {
  useDeactivateInternalUserMutation,
  useDeleteInternalUserMutation,
  useListInternalUsersQuery
} from 'redux/reducers/internal-user';
import { IInternalUserInput } from './schema';
import editIcon from '../../../assets/images/icons/edit.png';
import { FcUnlock } from 'react-icons/fc';
import { useDecodeToken } from 'hooks/useStorage';
import { Button } from '@mui/material';
import { useResetPasswordToDefault } from 'service/auth.query';
import ConfirmationModal from 'components/ui/ConfirmationModal/confirmation-modal';
import { toast } from 'react-toastify';

interface IInternalUserTable {
  onEditClicked: (data: IInternalUserInput) => void;
  onViewClick: (data: IInternalUserInput) => void;
  searchQuery: string;
  onUnBlock: (id: number) => void;
  userType: string
}

type ConfirmActionType = 'reset' | 'deactivate' | 'delete';

interface IConfirmAction {
  type: ConfirmActionType;
  id: number;
  name: string;
}

const confirmActionConfig: Record<
  ConfirmActionType,
  { title: string; btnText: string; icon: string; description: string }
> = {
  reset: {
    title: 'Reset Password Confirmation',
    btnText: 'Reset',
    icon: 'ri-lock-password-line text-warning',
    description: 'This action will reset password to default.'
  },
  deactivate: {
    title: 'Deactivate User Confirmation',
    btnText: 'Deactivate',
    icon: 'ri-user-forbid-line text-warning',
    description: 'This user will no longer be able to log in until reactivated.'
  },
  delete: {
    title: 'Delete User Confirmation',
    btnText: 'Delete',
    icon: 'ri-delete-bin-line text-danger',
    description: 'This will permanently remove the user. This action cannot be undone.'
  }
};

const InternalUserManagementTable: FC<IInternalUserTable> = ({
  onEditClicked,
  onViewClick,
  searchQuery = '',
  onUnBlock,
  userType
}) => {
  const [pageNo, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [confirmAction, setConfirmAction] = useState<IConfirmAction | null>(null);

  const closeConfirmation = () => setConfirmAction(null);

  const { data: internalUserData, isLoading, isSuccess } = useListInternalUsersQuery(
    {

      searchQuery,
      pageNo,
      pageSize,
      userType

    }
  );
  const totalRows = internalUserData?.data && internalUserData?.data.length > 0 ? internalUserData?.data?.[0].row_total : 0;

  const totalPages = Math.ceil(totalRows / 10);
  const canNext = pageNo < totalPages;
  const canPrevious = pageNo > 1;
  const decodedToken: any = useDecodeToken();
  const isSuperAdmin = decodedToken?.IsSuperUser == 'True';

  const { mutate: resetPassword, isLoading: isResetting } = useResetPasswordToDefault()
  const [deactivateUser, { isLoading: isDeactivating }] = useDeactivateInternalUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteInternalUserMutation();

  const isActionLoading = isResetting || isDeactivating || isDeleting;

  const getFullName = (row: any) =>
    [row?.first_name, row?.middle_name, row?.last_name].filter(Boolean).join(' ');

  const onConfirmAction = () => {
    if (!confirmAction) return;
    const { type, id } = confirmAction;

    if (type === 'reset') {
      resetPassword(id, {
        onSuccess: (res) => {
          if (res?.data.code == 200) {
            toast.success(res.data.message)
            closeConfirmation()
          } else {
            toast.error(res?.data.message)

          }
        }
      })
      return;
    }

    const request = type === 'deactivate' ? deactivateUser(id) : deleteUser(id);
    request
      .unwrap()
      .then((res: any) => {
        toast.success(
          res?.message ?? `User ${type === 'deactivate' ? 'deactivated' : 'deleted'} successfully`
        );
        closeConfirmation();
      })
      .catch((err: any) => {
        toast.error(
          err?.data?.message ??
          `Error in ${type === 'deactivate' ? 'deactivating' : 'deleting'} user`
        );
      });
  };

  const columns: any = [
    {
      Header: 'S.N',
      accessor: 'sn',
      Cell: (data: Cell<IInternalUserInput>) => {
        return <>{pageNo === 1 ? data.row.index + 1 : data.row.index + (pageNo - 1) * 10 + 1}</>;
      }
    },
    {
      Header: 'First Name',
      accessor: 'first_name'
    },
    {
      Header: 'Middle Name',
      accessor: 'middle_name'
    },
    {
      Header: 'Last Name',
      accessor: 'last_name'
    },
    {
      Header: 'Email',
      accessor: 'email'
    },
    {
      Header: 'Mobile No',
      accessor: 'mobile_number'
    },
    {
      Header: 'User Type',
      Cell: (data: any) => {
        return <>{data.row.original.user_type == '1' ? 'Internal' : 'External'}</>;
      }
      //accessor: 'user_type'
    },
    {
      Header: 'Created By',
      accessor: 'created_by_name'
    },
    {
      Header: 'Status',
      Cell: (data: any) => {
        const isActive = data.row?.original?.is_active ?? true;
        return (
          <span className={`badge ${isActive ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
            {isActive ? 'Active' : 'Deactivated'}
          </span>
        );
      }
    },
    {
      Header: '  Action',
      Cell: (data: any) => {
        const row = data.row?.original;
        const isActive = row?.is_active ?? true;
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              src={ViewIcon}
              style={{ cursor: 'pointer' }}
              onClick={() => onViewClick({ ...data.row.original })}
            />

            <img
              src={editIcon}
              style={{ cursor: 'pointer' }}
              onClick={() => onEditClicked({ ...data.row.original, role: data.row.original.role })}
            />
            {data.row?.original?.user__is_blocked === true ? (
              <FcUnlock
                size={20}
                cursor={'pointer'}
                onClick={() => onUnBlock(data.row.original.main_user_id)}
              />
            ) : null}

            {isSuperAdmin ? <Button
              onClick={() =>
                setConfirmAction({ type: 'reset', id: row?.main_user_id, name: getFullName(row) })
              }
            >Reset</Button> : ""}
            {isSuperAdmin ? (
              <div className="dropdown">
                <a
                  href="#"
                  role="button"
                  id={`internalUserActionMenu-${row?.id}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="ri-more-2-fill fs-18"></i>
                </a>

                <ul
                  className="dropdown-menu fixed z-[10] position-fixed-important"
                  aria-labelledby={`internalUserActionMenu-${row?.id}`}
                >
                  <li>
                    <a
                      className="dropdown-item"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setConfirmAction({
                          type: 'deactivate',
                          id: row?.id,
                          name: getFullName(row)
                        });
                      }}
                    >
                      <i className="ri-user-forbid-line text-warning me-2"></i>
                      Deactivate user
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setConfirmAction({ type: 'delete', id: row?.id, name: getFullName(row) });
                      }}
                    >
                      <i className="ri-delete-bin-line text-danger me-2"></i>
                      Delete user
                    </a>
                  </li>
                </ul>
              </div>
            ) : null}
          </div>
        );
      }
    }
  ];
  return (
    <div>

      {confirmAction && <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={closeConfirmation}
        onConfirm={onConfirmAction}
        title={confirmActionConfig[confirmAction.type].title}
        btnText={confirmActionConfig[confirmAction.type].btnText}
        isLoading={isActionLoading}
      >
        <div className="p-3 text-center">
          <i className={`${confirmActionConfig[confirmAction.type].icon} display-5`}></i>
          <h4 className="mt-3">Are you sure?</h4>
          {confirmAction.name && <p className="mb-1 fw-semibold">{confirmAction.name}</p>}
          <p className="text-muted">{confirmActionConfig[confirmAction.type].description}</p>
        </div>
      </ConfirmationModal>}
      <DataTable
        columns={columns}
        // data={internalUserData?.data || []}
        data={internalUserData?.data || []}
        search={false}
        isLoading={isLoading}
        pagination={{
          canPrevious,
          canNext,
          prevPage: () => {
            if (canPrevious) {
              setPage((prev) => prev - 1);

            }
          },
          nextPage: () => {
            if (canNext) {
              setPage((prev) => prev + 1);
            }
          }
        }}
      />
    </div>
  );
};

export default InternalUserManagementTable;
